import { useMutation, useQuery, useQueryClient } from "wagmi";
import axios from "axios";
import { env } from "~/env.mjs";
import { useContext } from "react";
import { NotificationContext } from "~/context/NotificationContext";
import useUser from "~/lib/useUser";
import { useRouter } from "next/router";
import { type RawUser } from "~/pages/api/auth/user";

export const useUserConsent = () => {
  const { user, isLoading: userLoading } = useUser();
  const queryFn = async () => {
    const { data } = await axios.get<{
      userConsent: boolean;
    }>(`${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/user/user-consent`, {
      headers: {
        Authorization: `Bearer ${user?.accessToken || ""}`,
      },
    });

    return data;
  };
  return useQuery(["userConsent"], queryFn, {
    enabled: !userLoading && !!user?.accessToken,
    retry: 2,
  });
};

export const useConsentUser = () => {
  const { push, query } = useRouter();
  const { redirect } = query;
  const { user, mutateUser } = useUser();
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();
  const { data } = useUserConsent();

  const updateUserConsent = async (userConsentForm: {
    userConsent: boolean;
  }) => {
    try {
      const response = await axios.patch<{ userConsent?: RawUser }>(
        `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/user/user-consent`,
        {
          userConsent: userConsentForm.userConsent ?? data?.userConsent,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken || ""}`,
          },
        }
      );

      if (!response.data) {
        showNotification(
          "An error occurred while processing your request. Please try again.",
          "error"
        );
        return;
      }

      if (user && response?.data?.userConsent?.userConsent) {
        user.userConsent = response.data.userConsent.userConsent;
        await mutateUser(user);
      }

      await queryClient.invalidateQueries(["userConsent"]);
      showNotification("Terms and conditions accepted", "success");

      if (redirect) {
        void push(redirect.toString());
        return;
      }

      void push("/");
      return;
    } catch (error) {
      showNotification(
        "An error occurred while processing your request. Please try again.",
        "error"
      );
    }
  };

  return useMutation({
    mutationFn: updateUserConsent,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["userConsent"] }),
  });
};
